// src/components/Swimming/SwimmingSummary.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { calculateSwimmingTSS } from '../../utils/tssSwimmingUtils';
import { calculateSwimmingVO2max } from '../../utils/vo2SwimmingUtils';
import { calculateSwimmingPace } from '../../utils/paceSwimmingUtils';
import { calculateSwolf } from '../../utils/swolfUtils';
import '../styles/Summary.css';
import { useTranslation } from '../../i18n';
import API_BASE_URL from '../../config/api';

const SwimmingSummary = ({ fileId, onSummaryData }) => {
  const { t, language } = useTranslation();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const convertSecondsToTime = (seconds) => {
    const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
    const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
    const s = String(Math.floor(seconds % 60)).padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  useEffect(() => { setData(null); }, [fileId]);

  useEffect(() => {
    console.log('🔄 fileId en SwimmingSummary:', fileId);
    if (!fileId) return;
    let isCancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res         = await axios.get(`${API_BASE_URL}/api/fit/parsed-data/${fileId}`);
        const sessionData = Array.isArray(res.data.sessions) ? res.data.sessions[0] : res.data.session;
        const session     = sessionData || {};
        const activity    = res.data.activity || {};
        const lapsData    = res.data.laps || [];
        const lengths     = res.data.lengths || [];

        const activeLaps = Array.isArray(lapsData)
          ? lapsData.filter(lap => lap.total_distance > 0 && lap.total_timer_time > 0)
          : [];

        let movementTime = 0, movementDistance = 0;
        if (activeLaps.length > 0) {
          movementTime     = activeLaps.reduce((sum, lap) => sum + (lap.total_timer_time || 0), 0);
          movementDistance = activeLaps.reduce((sum, lap) => sum + (lap.total_distance   || 0), 0);
        } else {
          movementTime     = session.total_timer_time || 0;
          movementDistance = session.total_distance   || 0;
        }

        const distanceMeters     = session.total_distance || 0;
        const totalStrokes       = session.total_cycles || lengths.reduce((sum, len) => sum + (len.total_strokes || 0), 0);
        const bestDistance       = Math.max(...lapsData.map(lap => lap.total_distance || 0));
        const bestTime           = Math.min(...lapsData.map(lap => lap.total_timer_time || Infinity));
        const bestPace           = bestDistance > 0 && bestTime < Infinity ? calculateSwimmingPace(bestDistance, bestTime) : null;
        const strokesPerMin      = totalStrokes && movementTime ? (totalStrokes / (movementTime / 60)).toFixed(1) : '-';
        const avgStrokesPerLength = lengths.length > 0
          ? (lengths.reduce((sum, l) => sum + (l.total_strokes || 0), 0) / lengths.length).toFixed(1)
          : '-';

        const strokesPer100mData = lapsData.map((lap) => {
          const strokes = lap.total_strokes ?? lap.total_cycles ?? 0;
          const dist    = lap.total_distance ?? 0;
          return strokes > 0 && dist > 0 ? (strokes / dist) * 100 : null;
        }).filter(v => v !== null);

        const avgStrokesPer100m = strokesPer100mData.length > 0
          ? (strokesPer100mData.reduce((a, b) => a + b, 0) / strokesPer100mData.length).toFixed(1)
          : '-';

        const rawTss = calculateSwimmingTSS(res.data, language);
        const tssData = { tss: isFinite(rawTss?.tss) ? rawTss.tss : NaN, message: rawTss?.message || t('swimming.notAvailable') };
        const swolf = calculateSwolf(lapsData);
        const { vo2max, message: vo2message } = calculateSwimmingVO2max(distanceMeters, movementTime);
        const pace = calculateSwimmingPace(movementDistance, movementTime);

        if (!isCancelled) {
          setData({
            date: new Date(activity.timestamp).toLocaleString(),
            sport: `${session?.sport || ''}${session?.sub_sport ? ' - ' + session.sub_sport : ''}`,
            duration:         convertSecondsToTime(session.total_timer_time || 0),
            movementDuration: convertSecondsToTime(movementTime),
            elapsedTime:      convertSecondsToTime(session.total_elapsed_time || 0),
            distance: Math.round(distanceMeters),
            calories: session.total_calories || '-',
            activeCalories:  session.active_calories  || '-',
            restingCalories: session.resting_calories || '-',
            avgHR:    session.avg_heart_rate || '-',
            maxHR:    session.max_heart_rate || '-',
            cadence:  session.avg_cadence    || '-',
            laps:     session.num_laps || lapsData.length || 0,
            lengths:  session.num_active_lengths || lengths.length || '-',
            poolLength: session.pool_length !== undefined && session.pool_length !== null ? `${session.pool_length} m` : '-',
            trainingEffect:  session.total_training_effect || '-',
            anaerobicEffect: session.total_anaerobic_effect !== undefined && session.total_anaerobic_effect !== null ? session.total_anaerobic_effect : '-',
            tss: tssData.tss, tssMessage: tssData.message,
            vo2max, vo2message, pace, swolf,
            totalStrokes: totalStrokes || '-',
            bestPace, strokesPerMin, avgStrokesPer100m, avgStrokesPerLength,
            intensityModerate: session.intensity_moderate_minutes || '-',
            intensityHigh:     session.intensity_high_minutes     || '-',
            intensityTotal:    session.intensity_minutes          || '-',
          });
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('❌ Error al cargar los datos:', err);
          setError(t('swimming.loadError'));
        }
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { isCancelled = true; };
  }, [fileId, t, language]);

  if (loading) return <div>{t('swimming.loading')}</div>;
  if (error)   return <div>{error}</div>;
  if (!data)   return <div>{t('swimming.noData')}</div>;

  const interpretStrokeEfficiency = (value) => {
    if (value === '-' || isNaN(value)) return t('swimming.notAvailable');
    const strokes = parseFloat(value);
    if (strokes <= 45) return t('swimming.strokeExcellent');
    if (strokes <= 52) return t('swimming.strokeGood');
    if (strokes <= 60) return t('swimming.strokeAcceptable');
    return t('swimming.strokeImprove');
  };

  return (
    <div className="summary-container">
      <h4 className="mb-3">{t('swimming.summaryTitle')}</h4>
      <div className="summary-grid">
        <div className="summary-item"><strong><i className="bi bi-calendar-event"></i> {t('swimming.date')}</strong><p>{data.date}</p></div>
        <div className="summary-item"><strong><i className="bi bi-droplet-half"></i> {t('swimming.sport')}</strong><p>{data.sport}</p></div>
        <div className="summary-item"><strong><i className="bi bi-clock-history"></i> {t('swimming.totalTime')}</strong><p>{data.duration}</p></div>
        <div className="summary-item"><strong><i className="bi bi-play-circle"></i> {t('swimming.movingTime')}</strong><p>{data.movementDuration}</p></div>
        <div className="summary-item"><strong><i className="bi bi-clock-fill"></i> {t('swimming.elapsedTime')}</strong><p>{data.elapsedTime}</p></div>
        <div className="summary-item"><strong><i className="bi bi-rulers"></i> {t('swimming.distance')}</strong><p>{data.distance} m</p></div>
        <div className="summary-item"><strong><i className="bi bi-fire"></i> {t('swimming.calories')}</strong><p>{data.calories} kcal</p></div>
        <div className="summary-item"><strong><i className="bi bi-heart-pulse"></i> {t('swimming.avgHR')}</strong><p>{data.avgHR} bpm</p></div>
        <div className="summary-item"><strong><i className="bi bi-heart-fill"></i> {t('swimming.maxHR')}</strong><p>{data.maxHR} bpm</p></div>
        <div className="summary-item"><strong><i className="bi bi-lightning"></i> {t('swimming.cadenceLabel')}</strong><p>{data.cadence} bpm</p></div>
        <div className="summary-item"><strong><i className="bi bi-arrow-repeat"></i> {t('swimming.laps')}</strong><p>{data.laps}</p></div>
        <div className="summary-item"><strong><i className="bi bi-sign-turn-right"></i> {t('swimming.lengths')}</strong><p>{data.lengths}</p></div>
        <div className="summary-item"><strong><i className="bi bi-bounding-box"></i> {t('swimming.poolLength')}</strong><p>{data.poolLength}</p></div>
        <div className="summary-item"><strong><i className="bi bi-activity"></i> {t('swimming.tte')}</strong><p>{data.trainingEffect}</p></div>
        <div className="summary-item"><strong><i className="bi bi-graph-up-arrow"></i> {t('swimming.tea')}</strong><p>{data.anaerobicEffect}</p></div>
        <div className="summary-item"><strong><i className="bi bi-beaker"></i> {t('swimming.tss')}</strong><p>{isFinite(data.tss) ? data.tss : t('swimming.notAvailable')}</p></div>
        <div className="summary-item"><strong><i className="bi bi-wind"></i> {t('swimming.vo2max')}</strong><p>{data.vo2max ? `${data.vo2max} ml/kg/min` : t('swimming.notAvailable')}</p></div>
        <div className="summary-item"><strong><i className="bi bi-stopwatch"></i> {t('swimming.avgPace')}</strong><p>{data.pace || t('swimming.notAvailable')}</p></div>
        <div className="summary-item"><strong><i className="bi bi-droplet"></i> {t('swimming.swolf')}</strong><p>{data.swolf || t('swimming.notCalculated')}</p></div>
        <div className="summary-item"><strong><i className="bi bi-hand-index-thumb"></i> {t('swimming.totalStrokes')}</strong><p>{data.totalStrokes}</p></div>
        <div className="summary-item">
          <strong><i className="bi bi-percent"></i> {t('swimming.strokesPer100m')}</strong>
          <p>{data.avgStrokesPer100m}</p>
          <small>{interpretStrokeEfficiency(data.avgStrokesPer100m)}</small>
        </div>
        <div className="summary-item"><strong><i className="bi bi-repeat"></i> {t('swimming.strokesPerMin')}</strong><p>{data.strokesPerMin} bpm</p></div>
      </div>
    </div>
  );
};

export default SwimmingSummary;
